<script setup lang="ts">
import { computed, PropType, ref, watch } from "vue";
import { DirectLink, Participant, Project, Trainer } from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";
import QuestionnaireLink from "../common/QuestionnaireLink.vue";
import TooltipButton from "../common/TooltipButton.vue";
import DirectLinkEditDialog from "./directlink/DirectLinkEditDialog.vue";
import DirectLinkShortLink from "./directlink/DirectLinkShortLink.vue";
import { useProjectDetailsDirectLinkStore } from "../../stores/projectDetailsDirectLink";
import { storeToRefs } from "pinia";
import { formatDate } from "@persolog/utils";

const { t } = useI18n();

const props = defineProps({
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

const emit = defineEmits(["update:project", "reload:participants"]);

const collapsed = ref(false);

const directLinkStore = useProjectDetailsDirectLinkStore();
const { availableQuantity, directLinkEditingVisible, deleteDirectLinkWarningDialogVisible } =
    storeToRefs(directLinkStore);

const persologBaseUrl = import.meta.env.VITE_APP_PERSOLOG_URL || "https://persolog.online";
const questionnairePath = import.meta.env.VITE_APP_QUESTIONNAIRE_FRONTEND_PATH || "/questionnaire";

const directLinkUrl = computed(() => {
    if (props.directLink && props.project) {
        return `${persologBaseUrl}${questionnairePath}/direct-link/${props.directLink.hash}?lang=${props.project.language}`;
    }
    return "";
});

const expiryText = computed(() =>
    props.directLink?.expiresAt ? formatDate(props.directLink.expiresAt, t("formats.datetime")) : "",
);

async function editDirectLinkContingent(amount: number, expiresAt: string | null = null) {
    if (!props.directLink) {
        return;
    }

    const updatedProject = await directLinkStore.editDirectLink(
        props.project,
        props.directLink,
        amount,
        props.trainer.id,
        expiresAt,
    );
    emit("update:project", updatedProject);
}

async function openEditDialog() {
    await directLinkStore.openEditDialog(props.trainer.id, props.project.defaultProduct);
}

async function deleteDirectLink() {
    const updatedProject = await directLinkStore.deleteDirectLink(props.project, props.trainer.id);
    emit("update:project", updatedProject);
    // Deleting the link of an anonymous project de-anonymizes its participants on the backend
    // (alias becomes the first name, generated emails are cleared), so the table must re-fetch.
    if (props.project.anonymous) {
        emit("reload:participants");
    }
}

watch(
    () => props.project,
    async (project) => {
        await directLinkStore.refreshAvailableQuantity(props.trainer.id, project.defaultProduct);
    },
    { immediate: true },
);
</script>

<template>
    <Panel
        :id="`direct-link_panel-${project.id}`"
        toggleable
        v-model:collapsed="collapsed"
        class="mt-4"
        :pt="{ header: { onClick: () => { collapsed = !collapsed; }, style: 'cursor: pointer' }, pcToggleButton: { root: { onClick: (e: MouseEvent) => e.stopPropagation() } } }"
    >
        <template #header>
            <div class="direct-link-heading-row">
                <span class="mb-0 p-panel-title">
                    {{ t("project-details.direct-link.heading-long") }}
                </span>
            </div>
        </template>
        <span
            :id="`project-details-direct-link_ratio-information-${project.id}`"
            class="direct-link-ratio"
        ><strong>{{ t("project-configuration.directLink.contingent")}}:</strong> {{ t("project-configuration.directLink.usedRatio", { used: participants.length, total: directLink?.quantity ?? 0 }) }}</span>
        <TooltipButton
            :content="t('project-configuration.directLink.usedRatioTooltip')"
            background-color="white"
        />
        <Button
            :id="`project-details-direct-link_btn_edit-${project.id}`"
            severity="secondary"
            icon="pi pi-pencil"
            size="small"
            class="m-1"
            text
            :label="t('project-configuration.directLink.edit')"
            v-tooltip="t('project-configuration.directLink.changeAmountTooltip')"
            @click="openEditDialog()"
        />
        <div :id="`project-details-direct-link_expiry-${project.id}`" class="text-sm mt-1 mb-2">
            <i class="pi pi-clock mr-1"></i>
            <template v-if="expiryText">
                {{ t("project-configuration.directLink.expiresOn", { date: expiryText }) }}
            </template>
            <template v-else>
                {{ t("project-configuration.directLink.noExpiry") }}
            </template>
        </div>
        <QuestionnaireLink
            :url="directLinkUrl"
            :qr-url="directLink?.shortUrl || directLinkUrl"
            :show-label="false"
        >
            <template #after-actions>
                <Button
                    :id="`project-details-direct-link_btn_delete-${project.id}`"
                    severity="danger"
                    class="ml-1"
                    icon="pi pi-trash"
                    outlined
                    @click="directLinkStore.openDeleteWarning()"
                    v-tooltip.top="{ value: t('project-configuration.deleteDirectLink'), showDelay: 500 }"
                />
            </template>
            <template #qr-extra>
                <DirectLinkShortLink
                    :project="project"
                    :direct-link="directLink"
                    :trainer="trainer"
                    :target-url="directLinkUrl"
                    @update:project="(p) => emit('update:project', p)"
                />
            </template>
        </QuestionnaireLink>
    </Panel>

    <DirectLinkEditDialog
        v-if="directLinkEditingVisible"
        :participants="participants"
        :direct-link="directLink"
        :available-quantity="availableQuantity"
        edit
        @edit:directlink="editDirectLinkContingent"
        @close:dialog="directLinkStore.closeEditDialog()"
    />

    <Dialog
        v-model:visible="deleteDirectLinkWarningDialogVisible"
        modal
        :header="t('project-configuration.deleteDirectLink')"
        :style="{ width: '30rem' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <span
            v-if="!project.anonymous"
            v-html="t('project-configuration.directLinkDeleteWarning')"
            class="p-text-secondary block mb-8"
        />
        <span
            v-else
            v-html="t('project-configuration.directLinkDeleteAnonymousWarning')"
            class="p-text-secondary block mb-8"
        />
        <div class="flex justify-center gap-2">
            <Button
                type="button"
                :label="t('buttons.cancel')"
                severity="secondary"
                @click="directLinkStore.closeDeleteWarning()"
            />
            <Button
                :id="`project-details-direct-link_btn_confirm-delete-${project.id}`"
                type="button"
                :label="t('buttons.ok')"
                severity="primary"
                @click="deleteDirectLink"
            />
        </div>
    </Dialog>
</template>

<style scoped>
.direct-link-container {
    margin-top: 0.75rem;
}

.direct-link-heading-row {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
}
</style>
